import React, { useCallback, useEffect, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { emptyPartnerDraft } from './shippingPartners';
import { useSaveButtonLabel } from './useSaveButtonLabel';

export default function AdminShippingPartnersSettings({ sessionToken, setStatus, onSaved }) {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const saveBtn = useSaveButtonLabel('Save partners');
  const [columnReady, setColumnReady] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await fetch('/api/admin_shipping_partners.php', { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setLoadError(d.error || 'Could not load couriers.');
        setPartners([]);
        return;
      }
      setPartners(Array.isArray(d.partners) ? d.partners : []);
      setColumnReady(d.column_ready !== false);
    } catch {
      setLoadError('Could not load couriers.');
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePartner = (index, field, value) => {
    setPartners((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const addPartner = () => {
    setPartners((prev) => [...prev, emptyPartnerDraft()]);
    setOpen(true);
  };

  const removePartner = (index) => {
    setPartners((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    saveBtn.startSaving();
    try {
      const r = await fetch('/api/admin_shipping_partners.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ partners }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setStatus(d.error || 'Could not save couriers.');
        saveBtn.markFailed();
        return;
      }
      setPartners(Array.isArray(d.partners) ? d.partners : []);
      setStatus('Shipping partners saved.');
      if (typeof onSaved === 'function') onSaved();
      saveBtn.markSaved();
    } catch {
      setStatus('Could not save couriers.');
      saveBtn.markFailed();
    }
  };

  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body py-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <h2 className="h6 mb-1">Shipping partners</h2>
            <p className="small text-muted mb-0">
              Couriers for tracking dropdown (e.g. Tirupati). Use {'{tracking}'} in the tracking URL template.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Manage partners'}
          </button>
        </div>
        {!columnReady ? (
          <p className="small text-warning mb-0 mt-2">
            Run <code>database/migrate_catalog_all.sql</code> to enable partner storage.
          </p>
        ) : null}
        {loadError ? (
          <p className="small text-warning mb-0 mt-2" role="alert">
            {loadError}
            {loadError.includes('Database unavailable') ? (
              <>
                {' '}
                Check MySQL is running and <code>public/includes/db_config.php</code> matches your database (see{' '}
                <code>LOCAL_DEV.md</code>).
              </>
            ) : null}
          </p>
        ) : null}
        {open ? (
          <div className="mt-3 border-top pt-3">
            {loading ? (
              <p className="small text-muted mb-0">Loading…</p>
            ) : (
              <>
                {partners.map((p, i) => (
                  <div key={`partner-${i}-${p.id || 'new'}`} className="row g-2 align-items-end mb-2">
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={p.name}
                        onChange={(e) => updatePartner(i, 'name', e.target.value)}
                        placeholder="Tirupati Courier"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1">Tracking URL template</label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        value={p.url_template}
                        onChange={(e) => updatePartner(i, 'url_template', e.target.value)}
                        placeholder="https://…/track?awb={tracking}"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-1">ID (auto)</label>
                      <input type="text" className="form-control form-control-sm" value={p.id} readOnly disabled />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger w-100"
                        onClick={() => removePartner(i)}
                        disabled={partners.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <div className="d-flex flex-wrap gap-2 mt-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addPartner}>
                    + Add partner
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm${saveBtn.isSaved ? ' btn-success' : ' btn-primary'}`}
                    onClick={save}
                    disabled={saveBtn.isSaving || !columnReady}
                  >
                    {saveBtn.label}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}
                    </div>
                    </div>
  );
}
